import * as React from "react";
import Drawer from "@sproutsocial/seeds-react-drawer";
import { PanelContext } from "./PanelContext";
import { PanelContent } from "./PanelContent";
import { PanelMobileActionsHeader } from "./PanelMobileActionsHeader";
import type { TypePanelShell } from "./PanelShared";

/**
 * Mobile bottom-sheet rendering, shared verbatim by both the styled and the
 * Tailwind shells. The mobile path always renders through `Drawer`, which is
 * itself already migrated to the hybrid pattern, so nothing here depends on the
 * root routing gate — the two shells render an identical mobile tree.
 */
export const PanelMobile = ({ shell }: { shell: TypePanelShell }) => {
  const {
    rest,
    children,
    closeButtonLabel,
    header,
    headerless,
    footer,
    title,
    titleId,
    zIndex,
    snapPoints,
    defaultSnapPoint,
    snapPoint,
    onSnapPointChange,
    snapToSequentialPoints,
    actions,
    actionsInHeader,
    ariaLabel,
    isPanelOpen,
    closePanel,
    enrichedValue,
    effectiveAriaLabelledBy,
  } = shell;

  // With `actionsInHeader`, Drawer suppresses the floating rail and expects the
  // consumer to render the actions inside a custom header. Auto-compose one
  // (title + action pills + close pill) when the consumer hasn't supplied their
  // own header; otherwise we'd drop `actions` on the floor.
  const shouldRenderActionsHeader =
    !headerless &&
    header == null &&
    actionsInHeader === true &&
    !!actions &&
    actions.length > 0;

  // Headerless suppresses the built-in header entirely; the children own all
  // chrome in that mode.
  let defaultMobileHeader: React.ReactNode = null;
  if (!headerless) {
    defaultMobileHeader = shouldRenderActionsHeader ? (
      <PanelMobileActionsHeader
        title={title}
        titleId={titleId}
        actions={actions!}
      />
    ) : (
      <Drawer.Header title={title} id={titleId} />
    );
  }

  return (
    <PanelContext.Provider value={enrichedValue}>
      <Drawer
        {...rest}
        open={isPanelOpen}
        onClose={closePanel}
        direction="bottom"
        closeButtonLabel={closeButtonLabel}
        zIndex={zIndex}
        snapPoints={snapPoints}
        defaultSnapPoint={defaultSnapPoint}
        snapPoint={snapPoint}
        onSnapPointChange={onSnapPointChange}
        snapToSequentialPoints={snapToSequentialPoints}
        actions={actions}
        actionsInHeader={actionsInHeader}
        aria-labelledby={effectiveAriaLabelledBy}
        aria-label={ariaLabel}
      >
        {header ?? defaultMobileHeader}
        <PanelContent>{children}</PanelContent>
        {footer}
      </Drawer>
    </PanelContext.Provider>
  );
};
